import { useState } from "react";
import api, { formatINR, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import DataTable from "@/components/DataTable";

export default function Expenses() {
  const { activeBranchId } = useAuth();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ category: "", amount: 0, description: "", date: new Date().toISOString().slice(0, 10), payment_method: "Cash" });
  const [reloadKey, setReloadKey] = useState(0);
  const load = () => setReloadKey((k) => k + 1);

  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/expenses", { ...form, branch_id: activeBranchId }); toast.success("Added"); setOpen(false); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => { if (!window.confirm("Delete?")) return; try { await api.delete(`/expenses/${id}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const columns = [
    { key: "date", label: "Date", sortable: true, render: (e) => <span className="text-slate-600">{e.date}</span> },
    { key: "category", label: "Category", sortable: true, render: (e) => <span className="font-medium">{e.category}</span> },
    { key: "description", label: "Description", render: (e) => <span className="text-slate-600">{e.description}</span> },
    { key: "payment_method", label: "Method", render: (e) => <span className="text-xs">{e.payment_method}</span> },
    { key: "amount", label: "Amount", sortable: true, className: "text-right", render: (e) => <span className="font-semibold tabular-nums">{formatINR(e.amount)}</span> },
    { key: "imported_from_tally", label: "Src", render: (e) => e.imported_from_tally ? <span className="text-[10px] uppercase text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5">Tally</span> : <span className="text-[10px] text-slate-400">Live</span> },
  ];
  const filters = [
    { key: "category", type: "select", label: "Category", options: [
      { value: "Salary", label: "Salary" }, { value: "Rent", label: "Rent" }, { value: "Fuel", label: "Fuel" },
      { value: "Utilities", label: "Utilities" }, { value: "Stationery", label: "Stationery" },
      { value: "Tailor Charges", label: "Tailor Charges" }, { value: "Transport", label: "Transport" },
      { value: "Other", label: "Other" },
    ] },
    { key: "payment_method", type: "select", label: "Method", options: [
      { value: "Cash", label: "Cash" }, { value: "Card", label: "Card" }, { value: "UPI", label: "UPI" }, { value: "Bank Transfer", label: "Bank Transfer" }, { value: "Journal", label: "Journal" },
    ] },
    { key: "imported", type: "select", label: "Source", options: [{ value: "true", label: "Imported (Tally)" }, { value: "false", label: "Live only" }] },
    { key: "date_from", type: "date", label: "From date" },
    { key: "date_to", type: "date", label: "To date" },
  ];

  return (
    <div className="space-y-6" data-testid="expenses-page">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold tracking-tight">Expenses</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-slate-900 hover:bg-slate-800 rounded-none" data-testid="add-expense-btn"><Plus className="w-4 h-4 mr-2" />Add Expense</Button></DialogTrigger>
          <DialogContent className="rounded-sm max-w-md">
            <DialogHeader><DialogTitle>New Expense</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div><Label>Category</Label><Input required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="rounded-none mt-1" placeholder="Rent, Salary..." data-testid="exp-cat" /></div>
              <div><Label>Amount (₹)</Label><Input required type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: parseFloat(e.target.value) || 0 })} className="rounded-none mt-1" data-testid="exp-amount" /></div>
              <div><Label>Date</Label><Input required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-none mt-1" /></div>
              <div><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-none mt-1" /></div>
              <DialogFooter><Button type="submit" className="bg-slate-900 hover:bg-slate-800 rounded-none">Save</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <DataTable
        endpoint="/expenses"
        columns={columns}
        filters={filters}
        searchPlaceholder="Search description, category, payment method..."
        extraQuery={{ branch_id: activeBranchId || undefined }}
        reloadKey={reloadKey}
        actions={(e) => (
          <Button size="sm" variant="ghost" className="rounded-none h-8 px-2 text-red-600" onClick={() => remove(e.id)}><Trash2 className="w-4 h-4" /></Button>
        )}
        dataTestid="expenses-table"
      />
    </div>
  );
}
